import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from 'components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import { StatusBadge } from 'components/StatusBadge';
import { StatusTimeline } from 'components/StatusTimeline';
import { MapPanel } from 'components/MapPanel';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import { formatPrice, formatDateTime } from 'lib/format';
import {
  ArrowLeft, MessageCircle, MapPin, Package, ShieldCheck, AlertTriangle,
  Check, X, Truck, CreditCard, Star, Camera, Edit3, BadgeCheck,
} from 'lucide-react';

const MAX_PICKUP_PHOTOS = 5;
const MAX_PICKUP_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_PICKUP_IMAGE_DATA_URL_LENGTH = 300000;

const compressPickupPhoto = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('FILE_READ_ERROR'));
  reader.onload = () => {
    const image = new Image();
    image.onerror = () => reject(new Error('IMAGE_DECODE_ERROR'));
    image.onload = () => {
      const scale = Math.min(1, 1200 / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      if (!context) return reject(new Error('CANVAS_UNAVAILABLE'));
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      for (let quality = 0.82; quality >= 0.42; quality -= 0.1) {
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        if (dataUrl.length <= MAX_PICKUP_IMAGE_DATA_URL_LENGTH) return resolve(dataUrl);
      }
      reject(new Error('IMAGE_TOO_LARGE'));
    };
    image.src = String(reader.result || '');
  };
  reader.readAsDataURL(file);
});

export default function MissionDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [mission, setMission] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewPunctuality, setReviewPunctuality] = useState(5);
  const [reviewCommunication, setReviewCommunication] = useState(5);
  const [reviewPackaging, setReviewPackaging] = useState(5);
  const [proofOpen, setProofOpen] = useState(false);
  const [proofCode, setProofCode] = useState('');
  const [proofRecipient, setProofRecipient] = useState('');
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDesc, setDisputeDesc] = useState('');
  const [myDispute, setMyDispute] = useState(null);
  const [disputeReplyText, setDisputeReplyText] = useState('');
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [priceMessage, setPriceMessage] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const discountStorageKey = `blablacolis_mission_discount_${id}`;
  const [discountCode, setDiscountCode] = useState(() => sessionStorage.getItem(discountStorageKey) || '');
  const [discountQuote, setDiscountQuote] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [paying, setPaying] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupCondition, setPickupCondition] = useState(t('missionDetail.defaultPickupCondition'));
  const [pickupWeight, setPickupWeight] = useState('');
  const [pickupPhotos, setPickupPhotos] = useState([]);
  const [pickupReservations, setPickupReservations] = useState('');
  const [pickupPhotoPreview, setPickupPhotoPreview] = useState(null);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [incidentKind, setIncidentKind] = useState('delayed');
  const [incidentDescription, setIncidentDescription] = useState('');
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignOptions, setReassignOptions] = useState({ deliverers: [], trips: [] });
  const [reassignTarget, setReassignTarget] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const watchIdRef = useRef(null);
  const lastLocationSentRef = useRef(0);

  const load = useCallback(() => api.get(`/missions/${id}`).then(r => setMission(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/reviews/mission/${id}/mine`).then((response) => setHasReviewed(Boolean(response.data?.reviewed))).catch(() => {});
  }, [id]);
  const loadMyDispute = useCallback(() => {
    api.get('/disputes').then((response) => {
      const found = (response.data || []).find((d) => d.mission_id === id);
      setMyDispute(found || null);
    }).catch(() => {});
  }, [id]);
  useEffect(() => { loadMyDispute(); }, [loadMyDispute]);
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (searchParams.get('payment') !== 'success' || !sessionId) return;
    api.post(`/missions/${id}/verify-payment-session`, { session_id: sessionId })
      .then((response) => {
        if (response.data?.payment_confirmed) {
          sessionStorage.removeItem(discountStorageKey);
          toast.success(t('missionDetail.paymentConfirmed'));
        }
        else toast.error(t('missionDetail.paymentConfirming'));
        load();
      })
      .catch((error) => toast.error(getApiErrorMessage(error, t('missionDetail.paymentVerifyFailed'))))
      .finally(() => setSearchParams({}, { replace: true }));
  }, [discountStorageKey, id, load, searchParams, setSearchParams]);

  useEffect(() => {
    if (!mission || !['in_progress', 'picked_up', 'in_transit', 'arrived_relay'].includes(mission.status)) return;
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, [mission?.status, load]);

  // Auto-start GPS sharing for the deliverer as soon as the mission is actively moving.
  useEffect(() => {
    if (!mission || mission.deliverer_id !== user.id) return;
    const active = ['in_progress', 'picked_up', 'in_transit', 'arrived_relay'].includes(mission.status);
    if (active && watchIdRef.current == null && navigator.geolocation) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const now = Date.now();
          if (now - lastLocationSentRef.current < 15000) return;
          lastLocationSentRef.current = now;
          api.post(`/missions/${id}/location`, {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 10000 },
      );
      setSharingLocation(true);
    } else if (!active && watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setSharingLocation(false);
    }
  }, [mission?.status, mission?.deliverer_id, user.id, id]);

  useEffect(() => () => {
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
  }, []);

  useEffect(() => {
    if (!mission) return;
    const targetId = user.id === mission.sender_id ? mission.deliverer_id : mission.sender_id;
    if (!targetId) return setOtherProfile(null);
    api.get(`/users/${targetId}`).then((r) => setOtherProfile(r.data)).catch(() => setOtherProfile(null));
  }, [mission, user.id]);

  useEffect(() => {
    const savedCode = sessionStorage.getItem(discountStorageKey);
    if (!mission || mission.status !== 'accepted' || discountQuote || !discountCode.trim()
        || !savedCode || savedCode.toUpperCase() !== discountCode.trim().toUpperCase()) return;
    let active = true;
    api.post(`/missions/${id}/apply-discount`, { code: discountCode.trim() })
      .then(response => {
        if (active) {
          setDiscountQuote(response.data);
          setDiscountCode(response.data.code);
        }
      })
      .catch(() => {
        sessionStorage.removeItem(discountStorageKey);
        if (active) setDiscountCode('');
      });
    return () => { active = false; };
  }, [discountCode, discountQuote, discountStorageKey, id, mission?.status]);

  if (!mission) return <DashboardShell><div>{t('missionDetail.loading')}</div></DashboardShell>;

  const isSender = user.id === mission.sender_id;
  const isDeliverer = user.id === mission.deliverer_id;
  const requesterId = mission.status_history?.[0]?.by || mission.sender_id;
  const responderId = requesterId === mission.sender_id ? mission.deliverer_id : mission.sender_id;
  const isRequester = user.id === requesterId;
  const isResponder = user.id === responderId;
  const otherName = isSender ? mission.deliverer_name : mission.sender_name;
  const otherId = isSender ? mission.deliverer_id : mission.sender_id;
  const otherContact = isSender ? mission.deliverer_contact : mission.sender_contact;
  const otherRating = Number(otherProfile?.rating || 0);
  const delivery = mission.delivery;
  const trip = mission.trip;
  const currentPrice = mission.final_price || mission.proposed_price;
  const canEditPrice = mission.status === 'pending';

  const updatePrice = async (e) => {
    e.preventDefault();
    if (!newPrice || Number(newPrice) <= 0) {
      toast.error(t('missionDetail.priceInvalid'));
      return;
    }
    setSavingPrice(true);
    try {
      await api.patch(`/missions/${id}/price`, {
        price: Number(newPrice),
        message: priceMessage || undefined,
      });
      toast.success(t('missionDetail.priceUpdated'));
      setPriceDialogOpen(false);
      setNewPrice('');
      setPriceMessage('');
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('missionDetail.priceUpdateFailed')));
    } finally {
      setSavingPrice(false);
    }
  };

  const updateStatus = async (status) => {
    setUpdatingStatus(status);
    try {
      const response = await api.post(`/missions/${id}/status`, { status });
      toast.success(response.data?.cancellation_message || `${t('missionDetail.statusUpdated')}: ${t(`missionDetail.status.${status}`)}`);
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('missionDetail.genericError')));
    } finally {
      setUpdatingStatus(null);
    }
  };

  const payNow = async () => {
    if (paying) return;
    setPaying(true);
    try {
      const r = await api.post(`/missions/${id}/pay`, discountQuote ? { code: discountQuote.code } : {});
      if (r.data?.paid) {
        sessionStorage.removeItem(discountStorageKey);
        toast.success(r.data.payment_method === 'wallet' ? t('missionDetail.paidWithWallet') : t('missionDetail.paymentConfirmedAlready'));
        refreshUser();
        load();
        return;
      }
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.error(t('missionDetail.paymentSessionUnavailable'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('missionDetail.paymentError')));
    } finally {
      setPaying(false);
    }
  };

  const applyDiscount = async () => {
    if (!discountCode.trim() || applyingDiscount) return;
    setApplyingDiscount(true);
    try {
      const response = await api.post(`/missions/${id}/apply-discount`, { code: discountCode.trim() });
      setDiscountQuote(response.data);
      setDiscountCode(response.data.code);
      sessionStorage.setItem(discountStorageKey, response.data.code);
      toast.success(t('missionDetail.discountApplied'));
    } catch (err) {
      setDiscountQuote(null);
      sessionStorage.removeItem(discountStorageKey);
      toast.error(getApiErrorMessage(err, t('missionDetail.discountError')));
    } finally {
      setApplyingDiscount(false);
    }
  };

  const submitProof = async () => {
    try {
      await api.post(`/missions/${id}/proof`, {
        validation_code: proofCode,
        recipient_name: proofRecipient,
      });
      toast.success(t('missionDetail.proofSaved'));
      setProofOpen(false);
      await updateStatus('delivered');
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('missionDetail.invalidCode')));
    }
  };

  const toggleShareLocation = () => {
    if (sharingLocation) {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setSharingLocation(false);
      return;
    }
    if (!navigator.geolocation) {
      toast.error(t('missionDetail.geolocationUnavailable'));
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const now = Date.now();
        if (now - lastLocationSentRef.current < 15000) return;
        lastLocationSentRef.current = now;
        api.post(`/missions/${id}/location`, {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }).catch(() => {});
      },
      () => toast.error(t('missionDetail.locationAccessFailed')),
      { enableHighAccuracy: true, maximumAge: 10000 },
    );
    setSharingLocation(true);
  };

  const submitReview = async () => {
    try {
      await api.post('/reviews', {
        mission_id: id,
        reviewee_id: otherId,
        rating: reviewRating,
        comment: reviewComment,
        punctuality: reviewPunctuality,
        communication: reviewCommunication,
        packaging: reviewPackaging,
      });
      toast.success(t('missionDetail.reviewPublished'));
      setHasReviewed(true);
      setReviewOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('missionDetail.genericError')));
    }
  };

  const submitDispute = async () => {
    try {
      await api.post('/disputes', {
        mission_id: id, reason: disputeReason, description: disputeDesc,
      });
      toast.success(t('missionDetail.disputeOpened'));
      setDisputeOpen(false);
      load();
      loadMyDispute();
    } catch (err) {
      toast.error(t('missionDetail.genericError'));
    }
  };

  const submitDisputeReply = async () => {
    if (!myDispute || !disputeReplyText.trim()) return;
    try {
      const response = await api.post(`/disputes/${myDispute.id}/reply`, { message: disputeReplyText.trim() });
      setMyDispute(response.data);
      setDisputeReplyText('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('missionDetail.replyUnavailable')));
    }
  };

  const submitIncident = async () => {
    if (!incidentDescription.trim()) return;
    try {
      await api.post(`/missions/${id}/incident`, { kind: incidentKind, description: incidentDescription.trim() });
      toast.success(t('missionDetail.incidentSaved'));
      setIncidentDescription('');
      setIncidentOpen(false);
      load();
    } catch (err) { toast.error(getApiErrorMessage(err, t('missionDetail.incidentSaveFailed'))); }
  };

  const openReassign = async () => {
    try {
      const response = await api.get(`/missions/${id}/reassignment-options`);
      setReassignOptions(response.data || { deliverers: [], trips: [] });
      setReassignOpen(true);
    } catch (err) { toast.error(getApiErrorMessage(err, t('missionDetail.reassignOptionsUnavailable'))); }
  };

  const submitReassign = async () => {
    if (!reassignTarget || !reassignReason.trim()) return;
    const [type, value] = reassignTarget.split(':');
    try {
      await api.post(`/missions/${id}/reassign`, { [type === 'trip' ? 'trip_id' : 'deliverer_id']: value, reason: reassignReason.trim() });
      toast.success(t('missionDetail.missionReassigned'));
      setReassignOpen(false);
      setReassignTarget('');
      setReassignReason('');
      load();
    } catch (err) { toast.error(getApiErrorMessage(err, t('missionDetail.reassignFailed'))); }
  };

  const savePickupChecklist = async () => {
    try {
      await api.post(`/missions/${id}/pickup-checklist`, {
        exterior_condition: pickupCondition,
        observed_weight_kg: pickupWeight ? Number(pickupWeight) : undefined,
        photo_urls: pickupPhotos,
        reservations: pickupReservations || undefined,
      });
      toast.success(t('missionDetail.checklistSent'));
      setPickupOpen(false);
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('missionDetail.checklistSaveFailed')));
    }
  };

  const addPickupPhotos = async (event) => {
    const files = Array.from(event.target.files || []);
    const available = MAX_PICKUP_PHOTOS - pickupPhotos.length;
    if (files.length > available) toast.error(t('missionDetail.photosMax').replace('{count}', String(available)));
    const selected = files.slice(0, Math.max(0, available));
    try {
      const photos = await Promise.all(selected.map(async (file) => {
        if (!file.type.startsWith('image/') || file.size > MAX_PICKUP_IMAGE_BYTES) throw new Error('INVALID_IMAGE');
        return compressPickupPhoto(file);
      }));
      setPickupPhotos((current) => [...current, ...photos]);
    } catch {
      toast.error(t('missionDetail.photoOptimizationFailed'));
    } finally {
      event.target.value = '';
    }
  };

  const acknowledgePickupChecklist = async () => {
    try {
      await api.post(`/missions/${id}/pickup-checklist/acknowledge`);
      toast.success(t('missionDetail.checklistValidated'));
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('missionDetail.validationImpossible')));
    }
  };

  return (
    <DashboardShell>
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
        <ArrowLeft size={16} className="mr-1" /> {t('missionDetail.back')}
      </Button>

      {/* Header sticky */}
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to={`/users/${otherId}`} aria-label={`${t('missionDetail.viewProfile')} ${otherName || t('missionDetail.thisUser')}`}>
              <Avatar className="hover:ring-2 hover:ring-primary transition-shadow">
                <AvatarFallback className="bg-secondary">{(otherName || 'U').split(' ').map(n => n[0]).slice(0,2).join('')}</AvatarFallback>
              </Avatar>
            </Link>
            <div>
              <div className="flex items-center gap-1.5">
                <Link to={`/users/${otherId}`} className="font-semibold hover:text-primary hover:underline">{otherName}</Link>
                {otherProfile?.deliverer_validated && <BadgeCheck size={14} className="text-emerald-600" />}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{t('missionDetail.missionNumber')} #{mission.id.slice(0, 8)}</span>
                {otherRating > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Star size={11} className="fill-amber-500 text-amber-500" /> {otherRating.toFixed(1)}
                    {otherProfile?.review_count > 0 && ` (${otherProfile.review_count})`}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={mission.status} className="" />
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-primary" data-testid="mission-price">{formatPrice(currentPrice)}</span>
              {canEditPrice && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1 text-xs"
                  onClick={() => {
                    setNewPrice(String(currentPrice));
                    setPriceDialogOpen(true);
                  }}
                  data-testid="edit-price-btn"
                >
                  <Edit3 size={13} /> {isRequester ? t('missionDetail.editOffer') : t('missionDetail.counterOffer')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Price modification banner if updated */}
      {isSender && mission.status === 'accepted' && !mission.payment_paid && (
        <div className="mb-4 p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <span className="font-semibold">{t('missionDetail.amountDue')} : {formatPrice(currentPrice)}</span>
            <p className="text-xs text-amber-800 mt-0.5">{t('missionDetail.amountAdjustmentNotice')}</p>
          </div>
          <Button size="sm" onClick={payNow} className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white gap-1.5">
            <CreditCard size={14} /> {t('missionDetail.payAmount')} {formatPrice(currentPrice)}
          </Button>
        </div>
      )}

      {/* Action bar */}
      <Card className="mb-4">
        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
          {/* Pending request actions */}
          {isResponder && mission.status === 'pending' && (
            <>
              <Button onClick={() => updateStatus('accepted')} disabled={updatingStatus === 'accepted'} data-testid="mission-accept-button"><Check size={14} className="mr-1" /> {updatingStatus === 'accepted' ? t('missionDetail.accepting') : t('missionDetail.accept')}</Button>
              <Button variant="destructive" onClick={() => updateStatus('refused')} data-testid="mission-refuse-button"><X size={14} className="mr-1" /> {t('missionDetail.refuse')}</Button>
            </>
          )}
          {isRequester && mission.status === 'pending' && (
            <Button variant="outline" onClick={() => updateStatus('cancelled')} data-testid="mission-cancel-request-button">
              <X size={14} className="mr-1" /> {t('missionDetail.cancelRequest')}
            </Button>
          )}
          {/* Sender pay after accept */}
          {isSender && mission.status === 'accepted' && (
            <div className="w-full space-y-3">
              <div className="rounded-lg border bg-secondary/30 p-3">
                <Label htmlFor="mission-discount-code" className="text-sm">{t('missionDetail.discountCode')}</Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    id="mission-discount-code"
                    value={discountCode}
                    placeholder={t('missionDetail.discountPlaceholder')}
                    onChange={event => { setDiscountCode(event.target.value.toUpperCase()); setDiscountQuote(null); sessionStorage.removeItem(discountStorageKey); }}
                    disabled={mission.payment_paid || paying || applyingDiscount}
                    data-testid="mission-discount-code-input"
                  />
                  {discountQuote ? (
                    <Button type="button" variant="outline" onClick={() => { setDiscountQuote(null); setDiscountCode(''); sessionStorage.removeItem(discountStorageKey); toast.success(t('missionDetail.discountRemoved')); }} disabled={paying} data-testid="mission-discount-remove-button">{t('missionDetail.removeDiscount')}</Button>
                  ) : (
                    <Button type="button" variant="outline" onClick={applyDiscount} disabled={!discountCode.trim() || applyingDiscount || mission.payment_paid} data-testid="mission-discount-apply-button">
                      {applyingDiscount ? t('missionDetail.applyingDiscount') : t('missionDetail.applyDiscount')}
                    </Button>
                  )}
                </div>
                {discountQuote && (
                  <div className="mt-3 space-y-1 text-sm" data-testid="mission-discount-summary">
                    <div className="flex justify-between"><span>{t('missionDetail.discountSubtotal')}</span><span>{formatPrice(discountQuote.original_amount)}</span></div>
                    <div className="flex justify-between text-emerald-700"><span>{t('missionDetail.discountLabel')} ({discountQuote.code})</span><span>−{formatPrice(discountQuote.discount_amount)}</span></div>
                    <div className="flex justify-between border-t pt-1 font-semibold"><span>{t('missionDetail.discountTotal')}</span><span>{formatPrice(discountQuote.amount_due)}</span></div>
                    <p className="pt-1 text-xs text-muted-foreground">{t('missionDetail.discountPlatformFunded')}</p>
                  </div>
                )}
              </div>
              <Button onClick={payNow} disabled={mission.payment_paid || paying} data-testid="mission-pay-button">
                <CreditCard size={14} className="mr-1" /> {paying ? t('missionDetail.applyingDiscount') : mission.payment_paid ? t('missionDetail.paymentConfirmed') : `${t('missionDetail.payByCard')} ${discountQuote ? formatPrice(discountQuote.amount_due) : ''}`}
              </Button>
            </div>
          )}
          {/* Deliverer progress */}
          {isDeliverer && mission.status === 'accepted' && (
            <Button onClick={() => updateStatus('in_progress')} disabled={!mission.payment_paid} data-testid="mission-start-button"><Truck size={14} className="mr-1" /> {t('missionDetail.startMission')}</Button>
          )}
          {isDeliverer && mission.status === 'accepted' && !mission.payment_paid && (
            <span className="text-xs text-muted-foreground">{t('missionDetail.waitingForSenderPayment')}</span>
          )}
          {(isSender || isDeliverer) && mission.status === 'accepted' && (
            <Button
              variant="outline"
              onClick={() => {
                const warning = isSender
                  ? t('missionDetail.cancelAcceptedSenderConfirm')
                  : t('missionDetail.cancelAcceptedDelivererConfirm');
                if (window.confirm(warning)) updateStatus('cancelled');
              }}
              disabled={updatingStatus === 'cancelled'}
              data-testid="mission-cancel-accepted-button"
            >
              <X size={14} className="mr-1" /> {t('missionDetail.cancelMission')}
            </Button>
          )}
          {isDeliverer && mission.status === 'in_progress' && (
            <>
              <Dialog open={pickupOpen} onOpenChange={setPickupOpen}>
                <DialogTrigger asChild><Button variant="outline">{t('missionDetail.pickupChecklist')}</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t('missionDetail.parcelConditionAtPickup')}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>{t('missionDetail.exteriorCondition')} *</Label><Textarea value={pickupCondition} onChange={(event) => setPickupCondition(event.target.value)} /></div>
                    <div><Label>{t('missionDetail.observedWeight')}</Label><Input type="number" min="0.1" step="0.1" value={pickupWeight} onChange={(event) => setPickupWeight(event.target.value)} /></div>
                    <div><Label>{t('missionDetail.parcelPhotos')}</Label><Input type="file" accept="image/*" capture="environment" multiple onChange={addPickupPhotos} /><div className="mt-2 grid grid-cols-4 gap-2">{pickupPhotos.map((photo, index) => <div key={photo} className="relative"><img src={photo} alt={`${t('missionDetail.parcelPhoto')} ${index + 1}`} className="h-16 w-full rounded object-cover" /><Button type="button" size="icon" variant="destructive" className="absolute -right-1 -top-1 h-5 w-5" onClick={() => setPickupPhotos((current) => current.filter((_, photoIndex) => photoIndex !== index))}><X size={12} /></Button></div>)}</div></div>
                    <div><Label>{t('missionDetail.reservations')}</Label><Textarea value={pickupReservations} onChange={(event) => setPickupReservations(event.target.value)} placeholder={t('missionDetail.reservationsPlaceholder')} /></div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setPickupOpen(false)}>{t('missionDetail.cancel')}</Button><Button onClick={savePickupChecklist}>{t('missionDetail.sendForValidation')}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Button onClick={() => updateStatus('picked_up')} disabled={!mission.pickup_acknowledged} data-testid="mission-pickup-button">{t('missionDetail.parcelCollected')}</Button>
            </>
          )}
          {isDeliverer && mission.status === 'picked_up' && (
            <Button onClick={() => updateStatus('in_transit')} data-testid="mission-transit-button">{t('missionDetail.inTransit')}</Button>
          )}
          {isDeliverer && (mission.status === 'in_transit' || mission.status === 'arrived_relay') && (
            <Dialog open={proofOpen} onOpenChange={setProofOpen}>
              <DialogTrigger asChild>
                <Button data-testid="mission-deliver-button"><Camera size={14} className="mr-1" /> {t('missionDetail.confirmDelivery')}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('missionDetail.deliveryProof')}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>{t('missionDetail.validationCode')}</Label>
                    <Input value={proofCode} onChange={e => setProofCode(e.target.value)} placeholder={t('missionDetail.sixDigits')} data-testid="proof-code-input" />
                  </div>
                  <div>
                    <Label>{t('missionDetail.recipientName')}</Label>
                    <Input value={proofRecipient} onChange={e => setProofRecipient(e.target.value)} data-testid="proof-recipient-input" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('missionDetail.validationCodeInstruction')}</p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setProofOpen(false)}>{t('missionDetail.cancel')}</Button>
                  <Button onClick={submitProof} data-testid="proof-submit-button">{t('missionDetail.validate')}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {(isSender || isDeliverer) && ['accepted', 'in_progress', 'picked_up', 'in_transit', 'arrived_relay', 'delayed', 'no_show', 'pickup_failed', 'recipient_absent', 'recipient_refused', 'return_requested'].includes(mission.status) && (
            <>
              <Dialog open={incidentOpen} onOpenChange={setIncidentOpen}>
                <DialogTrigger asChild><Button variant="outline"><AlertTriangle size={14} className="mr-1" /> {t('missionDetail.reportIncident')}</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t('missionDetail.reportIncident')}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={incidentKind} onChange={e => setIncidentKind(e.target.value)}>
                      <option value="delayed">{t('missionDetail.incident.delayed')}</option><option value="no_show">{t('missionDetail.incident.noShow')}</option><option value="pickup_failed">{t('missionDetail.incident.pickupFailed')}</option><option value="recipient_absent">{t('missionDetail.incident.recipientAbsent')}</option><option value="recipient_refused">{t('missionDetail.incident.recipientRefused')}</option><option value="return_requested">{t('missionDetail.incident.returnRequested')}</option>
                    </select>
                    <Textarea value={incidentDescription} onChange={e => setIncidentDescription(e.target.value)} placeholder={t('missionDetail.describeSituation')} />
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setIncidentOpen(false)}>{t('missionDetail.cancel')}</Button><Button onClick={submitIncident} disabled={!incidentDescription.trim()}>{t('missionDetail.save')}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              {isSender && ['delayed', 'no_show', 'pickup_failed', 'recipient_absent', 'recipient_refused', 'return_requested'].includes(mission.status) && <Button variant="outline" onClick={openReassign}>{t('missionDetail.reassign')}</Button>}
            </>
          )}
          <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>{t('missionDetail.reassignMission')}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={reassignTarget} onChange={e => setReassignTarget(e.target.value)}>
                  <option value="">{t('missionDetail.chooseDelivererOrTrip')}</option>
                  {reassignOptions.deliverers.map(item => <option key={`deliverer:${item.deliverer_id}`} value={`deliverer:${item.deliverer_id}`}>{item.name} ({item.rating || 0}/5)</option>)}
                  {reassignOptions.trips.map(item => <option key={`trip:${item.trip_id}`} value={`trip:${item.trip_id}`}>{item.route} - {item.name}</option>)}
                </select>
                <Textarea value={reassignReason} onChange={e => setReassignReason(e.target.value)} placeholder={t('missionDetail.reassignReason')} />
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setReassignOpen(false)}>{t('missionDetail.cancel')}</Button><Button onClick={submitReassign} disabled={!reassignTarget || !reassignReason.trim()}>{t('missionDetail.reassign')}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Review after delivered */}
          {mission.status === 'delivered' && (
            <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
              <DialogTrigger asChild>
                <Button variant="secondary" disabled={hasReviewed} data-testid="mission-review-button"><Star size={14} className="mr-1" /> {hasReviewed ? t('missionDetail.reviewAlreadyLeft') : t('missionDetail.leaveReview')}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('missionDetail.yourReview')} {otherName}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('missionDetail.overallRating')}</Label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(n => (
                        <button key={n} type="button" onClick={() => setReviewRating(n)} data-testid={`rating-${n}-button`}>
                          <Star size={28} className={n <= reviewRating ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground'} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: t('missionDetail.punctuality'), value: reviewPunctuality, set: setReviewPunctuality },
                      { label: t('missionDetail.communication'), value: reviewCommunication, set: setReviewCommunication },
                      { label: t('missionDetail.packaging'), value: reviewPackaging, set: setReviewPackaging },
                    ].map(({ label, value, set }) => (
                      <div key={label}>
                        <Label className="text-xs text-muted-foreground">{label}</Label>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(n => (
                            <button key={n} type="button" onClick={() => set(n)}>
                              <Star size={16} className={n <= value ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground'} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} placeholder={t('missionDetail.reviewCommentPlaceholder')} data-testid="review-comment-input" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setReviewOpen(false)}>{t('missionDetail.cancel')}</Button>
                  <Button onClick={submitReview} data-testid="review-submit-button">{t('missionDetail.publish')}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {/* Dispute */}
          {['picked_up', 'in_transit', 'arrived_relay', 'delivered'].includes(mission.status) && (
            <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="mission-dispute-button"><AlertTriangle size={14} className="mr-1" /> {myDispute ? t('missionDetail.viewDispute') : t('missionDetail.openDispute')}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{myDispute ? t('missionDetail.disputeInProgress') : t('missionDetail.openDispute')}</DialogTitle></DialogHeader>
                {myDispute ? (
                  <div className="space-y-3">
                    <div className="text-sm"><strong>{myDispute.reason}</strong><p className="text-muted-foreground">{myDispute.description}</p></div>
                    {(myDispute.replies || []).length > 0 && (
                      <div className="max-h-48 overflow-y-auto space-y-2 border-t pt-2">
                        {myDispute.replies.map((r, i) => (
                          <div key={i} className="text-sm">
                            <span className={`font-medium ${r.is_admin ? 'text-primary' : ''}`}>{r.is_admin ? 'Support ColiBuddy' : (r.author_name || t('missionDetail.you'))}</span>
                            <span className="text-xs text-muted-foreground ml-2">{formatDateTime(r.created_at)}</span>
                            <p className="text-muted-foreground">{r.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {myDispute.status !== 'closed' && (
                      <div className="flex gap-2">
                        <Input value={disputeReplyText} onChange={e => setDisputeReplyText(e.target.value)} placeholder={t('missionDetail.replyPlaceholder')} />
                        <Button onClick={submitDisputeReply} disabled={!disputeReplyText.trim()}>{t('missionDetail.send')}</Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      <div>
                        <Label>{t('missionDetail.reason')}</Label>
                        <Input value={disputeReason} onChange={e => setDisputeReason(e.target.value)} placeholder={t('missionDetail.disputeReasonPlaceholder')} data-testid="dispute-reason-input" />
                      </div>
                      <div>
                        <Label>{t('missionDetail.detailedDescription')}</Label>
                        <Textarea value={disputeDesc} onChange={e => setDisputeDesc(e.target.value)} data-testid="dispute-desc-input" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDisputeOpen(false)}>{t('missionDetail.cancel')}</Button>
                      <Button onClick={submitDispute} data-testid="dispute-submit-button">{t('missionDetail.send')}</Button>
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>
          )}
          {/* Chat */}
          <Button variant="outline" onClick={() => navigate(`/messages/${otherId}`)} data-testid="mission-chat-button">
            <MessageCircle size={14} className="mr-1" /> {t('missionDetail.chat')}
          </Button>

          {/* Edit price Dialog */}
          {canEditPrice && (
            <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{isRequester ? t('missionDetail.editPriceOffer') : t('missionDetail.makeCounterOffer')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={updatePrice} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {isRequester
                      ? t('missionDetail.editPriceDescription')
                      : t('missionDetail.counterOfferDescription')}
                  </p>
                  <div>
                    <Label htmlFor="new-price-input">{isRequester ? t('missionDetail.newPrice') : t('missionDetail.counterPrice')}</Label>
                    <Input
                      id="new-price-input"
                      type="number"
                      step="0.5"
                      min="1"
                      required
                      value={newPrice}
                      onChange={e => setNewPrice(e.target.value)}
                      placeholder={t('missionDetail.priceExample')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="price-message-input">{t('missionDetail.messageOrReasonOptional')}</Label>
                    <Input
                      id="price-message-input"
                      value={priceMessage}
                      onChange={e => setPriceMessage(e.target.value)}
                      placeholder={t('missionDetail.priceMessagePlaceholder')}
                      className="mt-1"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setPriceDialogOpen(false)}>
                      {t('missionDetail.cancel')}
                    </Button>
                    <Button type="submit" disabled={savingPrice}>
                      {savingPrice ? t('missionDetail.saving') : (isRequester ? t('missionDetail.updateMyOffer') : t('missionDetail.sendCounterOffer'))}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="timeline">
        <TabsList>
          <TabsTrigger value="timeline">{t('missionDetail.tracking')}</TabsTrigger>
          <TabsTrigger value="map">{t('missionDetail.map')}</TabsTrigger>
          <TabsTrigger value="details">{t('missionDetail.details')}</TabsTrigger>
        </TabsList>
        <TabsContent value="timeline">
          <Card>
            <CardHeader><CardTitle className="text-base">{t('missionDetail.missionStatus')}</CardTitle></CardHeader>
            <CardContent>
              <StatusTimeline status={mission.status} history={mission.status_history || []} />
              {mission.pickup_checklist && <div className="mt-6 border-t pt-4 space-y-3 text-sm"><div className="flex items-center justify-between gap-3"><h3 className="font-semibold">{t('missionDetail.pickupChecklist')}</h3><span className="text-xs text-muted-foreground">{mission.pickup_acknowledged ? t('missionDetail.acknowledgedBySender') : t('missionDetail.awaitingValidation')}</span></div><p><strong>{t('missionDetail.conditionLabel')} :</strong> {mission.pickup_checklist.exterior_condition}</p>{mission.pickup_checklist.observed_weight_kg && <p><strong>{t('missionDetail.observedWeightLabel')} :</strong> {mission.pickup_checklist.observed_weight_kg} kg</p>}{mission.pickup_checklist.reservations && <p><strong>{t('missionDetail.reservations')} :</strong> {mission.pickup_checklist.reservations}</p>}{(mission.pickup_checklist.photo_urls || []).length > 0 && <div><p className="mb-2 font-medium">{t('missionDetail.handoffPhotos')}</p><div className="grid grid-cols-3 sm:grid-cols-5 gap-2">{mission.pickup_checklist.photo_urls.map((photo, index) => <button type="button" key={photo} className="overflow-hidden rounded border focus:outline-none focus:ring-2 focus:ring-primary" onClick={() => setPickupPhotoPreview(photo)}><img src={photo} alt={`${t('missionDetail.pickupPhoto')} ${index + 1}`} className="h-20 w-full object-cover" /></button>)}</div></div>}{isSender && !mission.pickup_acknowledged && <Button size="sm" onClick={acknowledgePickupChecklist}>{t('missionDetail.validateChecklist')}</Button>}</div>}
              {isSender && mission.validation_code && ['accepted','in_progress','picked_up','in_transit'].includes(mission.status) && (
                <div className="mt-6 p-3 bg-amber-50 rounded-lg text-sm border border-amber-200">
                  <strong>{t('missionDetail.deliveryCodeToShare')}</strong>
                  <div className="text-2xl font-bold tracking-widest mt-1" data-testid="validation-code">{mission.validation_code}</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="map">
          {delivery && (
            <MapPanel
              departure={delivery.departure_city}
              arrival={delivery.arrival_city}
              current={mission.current_lat != null && mission.current_lng != null ? [mission.current_lat, mission.current_lng] : null}
              height="360px"
            />
          )}
          {isDeliverer && ['in_progress', 'picked_up', 'in_transit', 'arrived_relay'].includes(mission.status) && (
            <div className="mt-3 flex items-center gap-3">
              <Button variant={sharingLocation ? 'secondary' : 'outline'} size="sm" onClick={toggleShareLocation} data-testid="share-location-button">
                <MapPin size={14} className="mr-1" /> {sharingLocation ? t('missionDetail.stopLocationSharing') : t('missionDetail.resumeLocationSharing')}
              </Button>
              {sharingLocation && <span className="text-xs text-muted-foreground">{t('missionDetail.locationSharedAutomatically')}</span>}
            </div>
          )}
          {mission.location_updated_at && (
            <p className="text-xs text-muted-foreground mt-2">{t('missionDetail.lastLocationReceived')} : {formatDateTime(mission.location_updated_at)}</p>
          )}
        </TabsContent>
        <TabsContent value="details">
          <div className="grid sm:grid-cols-2 gap-4">
            {delivery && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Package size={16}/> {t('missionDetail.parcel')}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div><b>{delivery.title}</b></div>
                  <div className="text-muted-foreground">{delivery.description}</div>
                  <div>{t('missionDetail.departure')}: {delivery.departure_city}, {delivery.departure_country}</div>
                  <div>{t('missionDetail.arrival')}: {delivery.arrival_city}, {delivery.arrival_country}</div>
                  <div>{t('missionDetail.weight')}: {delivery.weight_kg} kg • {t('missionDetail.size')}: {delivery.size}</div>
                  {mission.contact_unlocked ? (
                    <div className="mt-4 border-t pt-3 space-y-1">
                      <div className="font-medium">{t('missionDetail.deliveryContactDetails')}</div>
                      {otherContact?.full_name && <div>{t('missionDetail.deliveryPartner')}: {otherContact.full_name}</div>}
                      {otherContact?.phone && <div>{t('missionDetail.phone')}: {otherContact.phone}</div>}
                      {otherContact?.email && <div>{t('missionDetail.email')}: {otherContact.email}</div>}
                      {delivery.recipient_name && <div>{t('missionDetail.recipient')}: {delivery.recipient_name}</div>}
                      {delivery.recipient_phone && <div>{t('missionDetail.phone')}: {delivery.recipient_phone}</div>}
                      {delivery.departure_address && <div>{t('missionDetail.departureAddress')}: {delivery.departure_address}</div>}
                      {delivery.arrival_address && <div>{t('missionDetail.arrivalAddress')}: {delivery.arrival_address}</div>}
                    </div>
                  ) : (
                    <div className="mt-4 border-t pt-3 text-xs text-muted-foreground">
                      {t('missionDetail.contactDetailsAfterPayment')}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {trip && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={16}/> {t('missionDetail.trip')}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div><b>{trip.departure_city} → {trip.arrival_city}</b></div>
                  <div className="text-muted-foreground">{formatDateTime(trip.departure_date)}</div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={Boolean(pickupPhotoPreview)} onOpenChange={(open) => !open && setPickupPhotoPreview(null)}>
        <DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>{t('missionDetail.pickupPhoto')}</DialogTitle></DialogHeader>{pickupPhotoPreview && <img src={pickupPhotoPreview} alt={t('missionDetail.enlargedPickupPhoto')} className="max-h-[70vh] w-full object-contain" />}</DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
